import { isEmpty, kebabCase } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { RegistrationPersonItemDataFragment } from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { formatTestId } from '../../../../data/utils/string.utils';
import { Button, ButtonType } from '../../../../elements/button/button';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';

/** registrations.registration-person[.bsn] */
const testId = formatTestId('registration-person', 'registrations');

const orDash = (input: string | null | undefined) =>
  isEmpty(input) ? '-' : input;

const join = (parts: (string | null | undefined)[], separator = ', ') =>
  orDash(parts.filter(part => !isEmpty(part)).join(separator));

type RegistrationPersonProps = {
  person: RegistrationPersonItemDataFragment;
  onPatient?: (value: RegistrationPersonItemDataFragment) => void;
  /** Only passed while the registration is open; the address is fixed after that. */
  onEditEmail?: (value: RegistrationPersonItemDataFragment) => void;
};

/**
 * One person out of the household. Every field the form asked for is shown
 * here, including the ones doccs does not model — the practice needs them to
 * enrol the person in its own record.
 *
 * The BSN is only rendered while the server still hands it out (Open); after
 * accepting or rejecting it comes back as null and the row disappears. The
 * server logs this view as the BSN access trail, which is why it lives in the
 * detail and never in the list.
 */
export function RegistrationPerson({
  person,
  onPatient,
  onEditEmail,
}: RegistrationPersonProps) {
  const { getFullName } = useFormatters();
  const intl = useIntl();

  const name = useMemo(
    () => join([person.initials, getFullName(person)], ' '),
    [person, getFullName]
  );

  return (
    <div
      data-test-id={testId}
      data-item-id={person.id}
      className="registration-modal-info__person"
    >
      <h3>
        {name}
        {!isEmpty(person.birthName) && (
          <span>
            <CustomFormattedMessage
              id="registrations.birth-name"
              values={{ name: person.birthName }}
            />
          </span>
        )}
      </h3>

      <h4>
        <CustomFormattedMessage id="common.birthdate" />
      </h4>
      <p>
        <CopyText text={formatDate(person.birthDate, 'dd-mm-YYYY')}>
          {formatDate(person.birthDate, 'dd-mm-YYYY')}
        </CopyText>
      </p>

      <h4>
        <CustomFormattedMessage id="common.gender" />
      </h4>
      <p>
        <CustomFormattedMessage
          id={`common.gender.${kebabCase(person.gender)}`}
        />
      </p>

      <h4>
        <CustomFormattedMessage id="common.email" />
      </h4>
      <p>
        {!isEmpty(person.email) ? (
          <a href={`mailto:${person.email}`} title={person.email}>
            {person.email}
          </a>
        ) : (
          <CustomFormattedMessage id="registrations.email.none" />
        )}
        {onEditEmail && (
          <Button
            testId={`${testId}.edit-email`}
            type={ButtonType.TEXT}
            onClick={() => onEditEmail(person)}
          >
            <CustomFormattedMessage id="registrations.email.edit" />
          </Button>
        )}
      </p>

      <h4>
        <CustomFormattedMessage id="common.phone" />
      </h4>
      <p>{orDash(person.phoneNumber)}</p>

      {!isEmpty(person.bsn) && (
        <>
          <h4>
            <CustomFormattedMessage id="registrations.bsn" />
          </h4>
          <p data-test-id={`${testId}.bsn`}>
            <CopyText text={person.bsn ?? ''}>{person.bsn}</CopyText>
          </p>
          <Message
            type={MessageType.WARNING}
            message={<CustomFormattedMessage id="registrations.bsn.notice" />}
          />
        </>
      )}

      {/* Only asked for a minor; for an adult there is nothing to show and a
          "-" would only be noise. */}
      {person.minorConsent !== null && person.minorConsent !== undefined && (
        <>
          <h4>
            <CustomFormattedMessage id="registrations.minor-consent" />
          </h4>
          <p>
            <CustomFormattedMessage
              id={person.minorConsent ? 'common.yes' : 'common.no'}
            />
          </p>
        </>
      )}

      {/* Empty means: lives at the address of the registration. */}
      {!isEmpty(person.street) && (
        <>
          <h4>
            <CustomFormattedMessage id="registrations.own-address" />
          </h4>
          <p>
            {join([
              `${person.street} ${person.houseNumber ?? ''}${person.houseNumberAddition ?? ''}`.trim(),
              `${person.postalCode ?? ''} ${person.city ?? ''}`.trim(),
            ])}
          </p>
        </>
      )}

      <h4>
        <CustomFormattedMessage id="registrations.pharmacy" />
      </h4>
      <p>{orDash(person.pharmacyName)}</p>

      <h4>
        <CustomFormattedMessage id="registrations.previous-gp" />
      </h4>
      <p>
        {person.previousGpInNetherlands === false ? (
          <CustomFormattedMessage id="registrations.previous-gp.none" />
        ) : (
          join([person.previousGpName, person.previousGpCity])
        )}
      </p>
      {!isEmpty(person.previousGpNote) && <p>{person.previousGpNote}</p>}

      <h4>
        <CustomFormattedMessage id="registrations.record-transfer-consent" />
      </h4>
      <p>
        {person.recordTransferConsent === null ||
        person.recordTransferConsent === undefined ? (
          <CustomFormattedMessage id="registrations.not-applicable" />
        ) : (
          <CustomFormattedMessage
            id={person.recordTransferConsent ? 'common.yes' : 'common.no'}
          />
        )}
      </p>
      {!isEmpty(person.recordTransferDeclineReason) && (
        <p>{person.recordTransferDeclineReason}</p>
      )}

      <h4>
        <CustomFormattedMessage id="registrations.insurance" />
      </h4>
      <p>
        {join([
          person.insuranceType
            ? intl.formatMessage({
                id: `registrations.insurance.${kebabCase(person.insuranceType)}`,
              })
            : null,
          person.insuranceCompany,
          person.insurancePolicyNumber,
        ])}
      </p>
      {!isEmpty(person.insuranceNote) && <p>{person.insuranceNote}</p>}

      <h4>
        <CustomFormattedMessage id="registrations.lsp-consent" />
      </h4>
      <p>
        {person.lspConsent === null || person.lspConsent === undefined ? (
          '-'
        ) : (
          <CustomFormattedMessage
            id={person.lspConsent ? 'common.yes' : 'common.no'}
          />
        )}
      </p>

      <h4>
        <CustomFormattedMessage id="registrations.contact-person" />
      </h4>
      <p>
        {join([
          person.contactPersonName,
          person.contactPersonRelation,
          person.contactPersonPhoneNumber,
        ])}
      </p>

      {!isEmpty(person.remarks) && (
        <>
          <h4>
            <CustomFormattedMessage id="registrations.remarks" />
          </h4>
          <p>{person.remarks}</p>
        </>
      )}

      {person.patientId && onPatient && (
        <Button
          testId="registration-open-patient"
          type={ButtonType.TEXT}
          onClick={() => onPatient(person)}
        >
          <CustomFormattedMessage id="registrations.button.open-patient" />
        </Button>
      )}
    </div>
  );
}
